import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { competitionDetailSchema } from '@competa/shared';
import { apiRequest, apiUrl } from '../../api/client';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { errorMessage } from '../../i18n/errors';
import { StatusBadge } from './CompetitionsPage';
import styles from './Competitions.module.css';
import { ImagesTab } from './ImagesTab';
import { ResultsTab } from './ResultsTab';

/** Reiter der Wettbewerbsansicht. */
const TABS = ['images', 'results', 'export'] as const;
/** Name eines Reiters. */
type Tab = (typeof TABS)[number];

/**
 * Wettbewerbsansicht mit den Reitern Bilder, Ergebnisse und Export; Abschließen und Wiedereröffnen
 * (Abschluss verändert keine Daten, offene Bilder gelten dann als abgelehnt) sowie Löschen der Teilnahme.
 */
export function CompetitionPage() {
  const { t } = useTranslation();
  const { id: rawId } = useParams();
  const id = Number(rawId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const tab: Tab = TABS.find((value) => value === params.get('tab')) ?? 'images';

  const detail = useQuery({
    queryKey: ['competition', id],
    queryFn: () => apiRequest('GET', `/competitions/${id}`, { schema: competitionDetailSchema }),
    enabled: Number.isInteger(id) && id > 0,
  });
  const toggleClosed = useMutation({
    mutationFn: (action: 'close' | 'reopen') =>
      apiRequest('POST', `/competitions/${id}/${action}`, { schema: competitionDetailSchema }),
    onSuccess: async (updated) => {
      queryClient.setQueryData(['competition', id], updated);
      await queryClient.invalidateQueries({ queryKey: ['competitions'] });
    },
  });
  const remove = useMutation({
    mutationFn: () => apiRequest('DELETE', `/competitions/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['competitions'] });
      navigate('/competitions');
    },
  });

  if (detail.isPending) return <p role="status">{t('common.loading')}</p>;
  if (detail.isError) {
    return (
      <section>
        <p role="alert" className={styles.error}>
          {errorMessage(t, detail.error)}
        </p>
        <Link to="/competitions">{t('competitions.back')}</Link>
      </section>
    );
  }
  const competition = detail.data;
  const closed = competition.status === 'closed';

  return (
    <section>
      <p>
        <Link to="/competitions">{t('competitions.back')}</Link>
      </p>
      <div className={styles.header}>
        <div>
          <h1>{competition.name}</h1>
          <div className={styles.meta}>
            {competition.fiapNumber && <span>{competition.fiapNumber}</span>}
            {competition.country && <span>{competition.country}</span>}
            <StatusBadge status={competition.status} />
          </div>
        </div>
        <div className={styles.actions}>
          {closed ? (
            <Button
              variant="secondary"
              disabled={toggleClosed.isPending}
              onClick={() => toggleClosed.mutate('reopen')}
            >
              {t('competitions.reopen')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              disabled={toggleClosed.isPending}
              onClick={() => toggleClosed.mutate('close')}
            >
              {t('competitions.close')}
            </Button>
          )}
          <Button variant="secondary" onClick={() => setConfirmDelete(true)}>
            {t('common.delete')}
          </Button>
        </div>
      </div>
      {(toggleClosed.error || remove.error) && (
        <p role="alert" className={styles.error}>
          {errorMessage(t, toggleClosed.error ?? remove.error)}
        </p>
      )}

      <div role="tablist" aria-label={t('competitions.sections')} className={styles.tabs}>
        {TABS.map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            id={`tab-${value}`}
            aria-selected={tab === value}
            aria-controls="tab-panel"
            className={styles.tab}
            onClick={() => setParams({ tab: value }, { replace: true })}
          >
            {t(`competitions.tab.${value}`)}
          </button>
        ))}
      </div>
      <div role="tabpanel" id="tab-panel" aria-labelledby={`tab-${tab}`}>
        {tab === 'images' && (
          <ImagesTab
            competitionId={id}
            status={competition.status}
            categories={competition.categories}
            submissions={competition.submissions}
          />
        )}
        {tab === 'results' && (
          <ResultsTab competitionId={id} closed={closed} submissions={competition.submissions} />
        )}
        {tab === 'export' && (
          <div className={styles.panel}>
            <p>{t('competitions.exportHint')}</p>
            <div className={styles.actions}>
              <a href={apiUrl(`/exports/competitions/${id}/results.csv`)}>
                {t('competitions.exportResults')}
              </a>
              <Link to="/export">{t('competitions.exportApplication')}</Link>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        title={t('competitions.deleteTitle')}
        message={t('competitions.deleteMessage', { name: competition.name })}
        confirmLabel={t('common.delete')}
        isOpen={confirmDelete}
        busy={remove.isPending}
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirmDelete(false)}
      />
    </section>
  );
}
